/**
 * Who is signed in.
 *
 * The token lives in `localStorage` and the account is re-read from the server on
 * every load rather than being trusted from the token. A token proves who signed
 * in, not that they may still sign in - a colleague deactivated this morning must
 * not still be working this afternoon.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError, api, auth as tokenStore } from '../lib/api/client'
import type { Me } from '../lib/api/client'
import { AuthContext, type AuthValue } from './authContext'

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [user, setUser] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const token = tokenStore.get()
    if (!token) {
      setLoading(false)
      return
    }
    api
      .me()
      .then(setUser)
      .catch(() => {
        tokenStore.clear()
        queryClient.clear()
      })
      .finally(() => setLoading(false))
  }, [queryClient])

  const signIn = useCallback(async (email: string, password: string) => {
    const session = await api.signIn(email, password)
    queryClient.clear()
    tokenStore.set(session.token)
    setUser(session.user)
  }, [queryClient])

  const acceptInvite = useCallback(async (token: string, fullName: string, password: string) => {
    const session = await api.acceptInvite(token, fullName, password)
    queryClient.clear()
    tokenStore.set(session.token)
    setUser(session.user)
  }, [queryClient])

  /** Re-read the account, after something that may have changed what it may do. */
  const refresh = useCallback(async () => {
    try {
      setUser(await api.me())
    } catch {
      // A failure here is not worth interrupting anyone for: the next request
      // that actually needs the session will get a 401 and sign them out.
    }
  }, [])

  const signOut = useCallback(() => {
    // Tell the server first, so the token stops working everywhere it was copied
    // rather than only here. The local clear-down is not conditional on it: a
    // teacher pressing Sign out on a shared staffroom machine must end up signed
    // out of this browser even if the network has just gone.
    api.signOut().catch(() => undefined)
    tokenStore.clear()
    queryClient.clear()
    setUser(null)
  }, [queryClient])

  // A 401 from anywhere means the session is gone. Handling it centrally stops
  // every screen having to think about it, and stops a teacher being left on a
  // page whose data will never load.
  useEffect(() => {
    const onRejection = (event: PromiseRejectionEvent) => {
      if (event.reason instanceof ApiError && event.reason.isSignedOut) {
        tokenStore.clear()
        queryClient.clear()
        setUser(null)
      }
    }
    window.addEventListener('unhandledrejection', onRejection)
    return () => window.removeEventListener('unhandledrejection', onRejection)
  }, [queryClient])

  const value = useMemo<AuthValue>(
    () => ({
      user,
      loading,
      signIn,
      acceptInvite,
      signOut,
      refresh,
      has: (...roles) => roles.some((role) => user?.roles.includes(role)),
    }),
    [user, loading, signIn, acceptInvite, signOut, refresh],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
